// Coordinate normalization adapter — painted [0,1]² ↔ server world-space.
//
// All painted zones use normalised coordinates where (0,0) is top-left and
// (1,1) is bottom-right. The server's legacy tilemap path uses pixel-based
// world-space coordinates. This adapter is the single place that converts
// between the two formats, and the key correctness gate for PaintedWorldCoordinator.

export interface NormalizedCoord {
  readonly nx: number;
  readonly ny: number;
}

export interface WorldCoord {
  readonly x: number;
  readonly y: number;
}

export function isNormalized(x: number, y: number): boolean {
  return (
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    x >= 0 &&
    x <= 1 &&
    y >= 0 &&
    y <= 1
  );
}

export function clampNormalized(x: number, y: number): NormalizedCoord {
  return {
    nx: Math.max(0, Math.min(1, Number.isFinite(x) ? x : 0.5)),
    ny: Math.max(0, Math.min(1, Number.isFinite(y) ? y : 0.5)),
  };
}

export function toNormalized(
  x: number,
  y: number,
  worldWidth: number,
  worldHeight: number,
): NormalizedCoord {
  if (worldWidth <= 0 || worldHeight <= 0) {
    return { nx: 0.5, ny: 0.5 };
  }
  return clampNormalized(x / worldWidth, y / worldHeight);
}

export function toWorldSpace(
  nx: number,
  ny: number,
  worldWidth: number,
  worldHeight: number,
): WorldCoord {
  const clamped = clampNormalized(nx, ny);
  return {
    x: clamped.nx * worldWidth,
    y: clamped.ny * worldHeight,
  };
}

export function normalizePositionEvent<T extends { x: number; y: number }>(
  event: T,
  worldWidth: number,
  worldHeight: number,
): T & { x: number; y: number } {
  if (isNormalized(event.x, event.y)) return event;
  const norm = toNormalized(event.x, event.y, worldWidth, worldHeight);
  return { ...event, x: norm.nx, y: norm.ny };
}
